<template>
  <PageProfileFeedLayout>
    <Card
      v-if="authStore?.userEntity && authStore.userEntity.id === props.user.id"
      class="!p-0 mb-4 border-b-4 sm:border-b-0 dark:border-black"
    >
      <PostAddEditForm
        :user="authStore.userEntity"
        sync
        @post="
          (post) => {
            appStore?.notify({
              preset: 'success.plain',
              detail:
                post.attributes.state === 'publish_delayed'
                  ? 'Post scheduled.'
                  : post.attributes.state === 'pending_media_upload'
                    ? 'Post will be published once video has finished processing.'
                    : 'Post has been submitted.',
              timer: 5000,
            });
            if (post.attributes.state === 'published')
              feedList?.refreshAllPosts();
          }
        "
      />
    </Card>
    <PostListVirtual
      :fetchNext="fetchNext"
      :noPostsIconLink="
        authStore?.userEntity?.id === props.user?.id
          ? getAbsoluteAssetLink('nami/stickers/okay.png')
          : getAbsoluteAssetLink('nami/stickers/sad.png')
      "
      :noPostsMessage="
        authStore?.userEntity?.id === props.user?.id
          ? 'You\'ve not posted yet, start a post?'
          : 'This user has not posted yet.'
      "
      ref="feedList"
    />
    <template #extra>
      <div class="space-y-4">
        <h1 class="text-xl font-bold mb-4 flex justify-between items-center">
          Organizations
          <NamiButton
            v-if="pending || organizations?.length"
            button-type="secondary"
            :icon="IconArrowRight"
            text
            pill
            small
            :href="`/${route.params.locale}/user/${user.attributes.username}/organizations`"
          />
        </h1>
        <NamiLoading v-if="pending" />
        <NoResults
          v-else-if="organizations?.length === 0"
          :message="translate('site.user.id.index.no_orgs')"
        />
        <template v-else>
          <OrganizationCard v-for="org in organizations" :org="org" no-follow />
        </template>
      </div>
    </template>
  </PageProfileFeedLayout>
</template>

<script setup lang="ts">
import {
  Organization,
  Post,
  type PostListParams,
  type UserEntity,
} from "~/src/api";
import { IconArrowRight } from "@tabler/icons-vue";

const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();
const appStore = nuxtApp.$app();
const route = useRoute();
const { t: translate } = nuxtApp.$i18n.global;
const maxOrgs = 5;

const props = defineProps<{ user: UserEntity }>();

const { pending, data: organizations } = useAsyncData(
  `user-${route.params.username}-index`,
  async () => {
    const [ownedOrgs, memberOrgs] = await Promise.all([
      Organization.search({ ownerId: props.user.id as string, limit: maxOrgs }),
      Organization.search({
        memberId: props.user.id as string,
        limit: maxOrgs,
      }),
    ]);

    const ownedOrgIds = ownedOrgs.data.map(({ id }) => id);

    return ownedOrgs.data
      .concat(memberOrgs.data.filter(({ id }) => !ownedOrgIds.includes(id)))
      .slice(0, maxOrgs);
  },
  {
    getCachedData: (key) => useNuxtData(key).data.value,
    watch: [() => props.user.id],
  },
);

async function fetchNext(params: PostListParams) {
  const token = (await authStore?.getToken()) ?? undefined;
  return await Post.getPosts(
    {
      ...params,
      creatorId: props.user.id,
      showOrganizationPosts: false,
    },
    token,
  );
}

const feedList = ref();

onActivated(() => {
  feedList.value?.checkForNewPosts();
});
</script>
